import { useState } from "react";
import { toast } from "sonner";
import { deleteRsvp } from "@/lib/api";
import { Users, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export const RsvpTable = ({ rsvps, onChange }) => {
  const [deleting, setDeleting] = useState(null);
  const total = rsvps.reduce((sum, r) => sum + (r.guest_count || 0), 0);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await deleteRsvp(id);
      toast.success("RSVP removed and item released.");
      await onChange();
    } catch {
      toast.error("Could not delete RSVP.");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="border-2 border-retro-ink bg-white shadow-retro-lg" data-testid="rsvp-table-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-retro-ink bg-retro-orange p-5">
        <h2 className="font-chango text-2xl text-white drop-shadow-[1px_1px_0px_#1A2A3A]">RSVPs</h2>
        <div className="flex items-center gap-2 border-2 border-retro-ink bg-retro-yellow px-4 py-1.5 shadow-retro-sm" data-testid="headcount-total">
          <Users className="h-5 w-5 text-retro-ink" />
          <span className="font-chango text-retro-ink">{total} coming</span>
        </div>
      </div>

      {rsvps.length === 0 ? (
        <p className="p-6 font-medium text-retro-ink/60" data-testid="rsvp-empty">No RSVPs yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="rsvp-table">
            <thead>
              <tr className="border-b-2 border-retro-ink bg-retro-cream font-chango text-sm text-retro-ink">
                <th className="p-4">Name</th>
                <th className="p-4">Guests</th>
                <th className="p-4">Bringing</th>
                <th className="p-4">When</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {rsvps.map((r) => (
                <tr key={r.id} className="border-b border-retro-ink/20 font-medium text-retro-ink" data-testid={`rsvp-row-${r.id}`}>
                  <td className="p-4 font-semibold">{r.name}</td>
                  <td className="p-4">{r.guest_count}</td>
                  <td className="p-4" data-testid={`rsvp-items-${r.id}`}>
                    {r.items?.length ? (
                      <div className="flex flex-wrap gap-1.5">
                        {r.items.map((it) => (
                          <span key={it.id} className="border-2 border-retro-ink bg-retro-cream px-2 py-0.5 text-xs font-semibold">
                            {it.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-retro-ink/60">Just themselves</span>
                    )}
                  </td>
                  <td className="p-4 text-sm text-retro-ink/70">{fmt(r.timestamp)}</td>
                  <td className="p-4">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-rsvp-${r.id}`}
                          disabled={deleting === r.id}
                          className="rounded-md border-2 border-retro-ink bg-white p-2 text-destructive transition-colors hover:bg-destructive hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="border-2 border-retro-ink shadow-retro-lg">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-chango">Delete this RSVP?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {r.name}'s spot will be removed{r.items?.length ? ` and ${r.items.length} claimed item${r.items.length > 1 ? "s" : ""} (${r.items.map((i) => i.name).join(", ")}) will be released back to the list.` : "."}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-rsvp">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            data-testid={`confirm-delete-rsvp-${r.id}`}
                            onClick={() => handleDelete(r.id)}
                            className="bg-destructive text-white hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
